import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as z from "zod";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { X, Search, UserPlus } from "lucide-react";
import { useUsers } from "@/hooks/users/useUsers";
import LoaderComponent from "@/components/common/LoaderComponent";
import PaginationComponent from "@/components/common/PaginationComponent";
import type { UserDashboard } from "@/services/api/users/usersGraphqlService";
import { useUserGroupsList } from "@/hooks/users/useUserGroupsList";
import { usersGraphqlService } from "@/services/api/users/usersGraphqlService";
import { useAuth } from "@/contexts/AuthContext";

const userGroupFormSchema = z.object({
  groupName: z
    .string()
    .min(1, "User group name is required")
    .max(100, "User group name must be less than 100 characters")
    .regex(
      /^[a-zA-Z0-9\s\-_]+$/,
      "User group name can only contain letters, numbers, spaces, hyphens, and underscores"
    ),
});

type UserGroupFormData = z.infer<typeof userGroupFormSchema>;

interface UserGroupFormProps {
  initialData?: Partial<UserGroupFormData & { assignedUserIds?: number[] }>;
  title: string;
  onSubmit: (data: { groupName: string; assignedUsers: UserDashboard[] }) => void;
  isSubmitting?: boolean;
  returnPath?: string;
  existingGroupId?: number; // For edit mode to exclude current group from duplicate check
}

export const UserGroupForm = ({ initialData, title, onSubmit, isSubmitting, returnPath, existingGroupId }: UserGroupFormProps) => {
  const isEditMode = !!existingGroupId;
  const navigate = useNavigate();
  const { hasuraToken } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserDashboard[]>([]);
  const [tempSelectedUsers, setTempSelectedUsers] = useState<UserDashboard[]>([]);
  const [loadingInitialUsers, setLoadingInitialUsers] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { userGroups } = useUserGroupsList();
  
  // Use the existing useUsers hook for fetching users
  const {
    users,
    loading,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    filterType,
    searchValue,
    setCurrentPage,
    setEntriesPerPage,
    setFilterType,
    setSearchValue,
    handleSearch,
    clearSearch,
  } = useUsers();
  
  const form = useForm<UserGroupFormData>({
    resolver: zodResolver(userGroupFormSchema),
    mode: "onChange",
    defaultValues: {
      groupName: initialData?.groupName || "",
    },
  });

  const watchedGroupName = form.watch("groupName");

  // Create stable reference for initial data comparison
  const initialDataRef = useMemo(() => ({
    groupName: initialData?.groupName || "",
    assignedUserIds: initialData?.assignedUserIds || [],
  }), []);

  // Check if form has changes (for edit mode)
  const hasFormChanges = useCallback(() => {
    if (!isEditMode) return true; // Always allow save in add mode

    // Compare group name
    const currentGroupName = watchedGroupName?.trim() || '';
    const initialGroupName = initialDataRef.groupName.trim();
    if (currentGroupName !== initialGroupName) return true;

    // Compare selected users
    const currentUserIds = selectedUsers.map(u => u.id).sort((a, b) => a - b);
    const initialUserIds = [...initialDataRef.assignedUserIds].sort((a, b) => a - b);

    if (currentUserIds.length !== initialUserIds.length) return true;
    for (let i = 0; i < currentUserIds.length; i++) {
      if (currentUserIds[i] !== initialUserIds[i]) return true;
    }

    return false;
  }, [watchedGroupName, selectedUsers, isEditMode, initialDataRef]);

  const formHasChanges = hasFormChanges();

  // Fetch and pre-populate assigned users when editing
  useEffect(() => {
    const fetchAssignedUsers = async () => {
      if (initialData?.assignedUserIds && initialData.assignedUserIds.length > 0 && hasuraToken) {
        setLoadingInitialUsers(true);
        try {
          const response = await usersGraphqlService.getUsersByIds(
            hasuraToken,
            initialData.assignedUserIds
          );
          
          if (response.success && response.data?.pep_vw_user_dashboard) {
            setSelectedUsers(response.data.pep_vw_user_dashboard);
          }
        } catch (error) {
          console.error('Failed to fetch assigned users:', error);
        } finally {
          setLoadingInitialUsers(false);
        }
      }
    };

    fetchAssignedUsers();
  }, [initialData?.assignedUserIds, hasuraToken]);

  // Filter out already selected users from the list
  const availableUsers = users.filter(user => 
    !selectedUsers.some(selected => selected.id === user.id)
  );

  // Auto-trigger search when searchValue changes (with debounce)
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Only search if modal is open
    if (!isModalOpen) return;

    // Clear search and show all users if search box is empty
    if (searchValue.length === 0) {
      clearSearch();
      return;
    }

    // Debounce search for 2+ characters
    if (searchValue.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch();
      }, 300);
    }

    // Cleanup
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchValue, isModalOpen]);

  // Trigger search when filter type changes
  useEffect(() => {
    if (isModalOpen && searchValue.length >= 2) {
      handleSearch();
    }
  }, [filterType]);

  const handleOpenModal = () => {
    setTempSelectedUsers([]);
    setSearchValue("");
    setFilterType("user_name");
    setCurrentPage(1);
    setIsModalOpen(true);
    // Trigger initial load of all users
    setTimeout(() => handleSearch(), 0);
  };

  const handleModalUserSelect = (user: UserDashboard) => {
    setTempSelectedUsers(prev => {
      const isSelected = prev.some(selected => selected.id === user.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleAddUsers = () => {
    setSelectedUsers(prev => [...prev, ...tempSelectedUsers]);
    setIsModalOpen(false);
    setTempSelectedUsers([]);
  };

  const handleUserRemove = (userId: number) => {
    setSelectedUsers(prev => prev.filter(user => user.id !== userId));
  };

  const handleSubmit = (data: UserGroupFormData) => {
    // Check if at least one user is selected
    if (selectedUsers.length < 1) {
      form.setError("groupName", {
        type: "manual",
        message: "At least 1 user must be added to the group"
      });
      return;
    }

    // Check for duplicate group name
    const trimmedGroupName = data.groupName.trim().toLowerCase();
    const isDuplicate = userGroups.some(group => {
      // Exclude current group in edit mode
      if (existingGroupId && group.user_group_id === existingGroupId) {
        return false;
      }
      return group.user_group_name.trim().toLowerCase() === trimmedGroupName;
    });

    if (isDuplicate) {
      form.setError("groupName", {
        type: "manual",
        message: "A user group with this name already exists"
      });
      return;
    }
    
    onSubmit({
      groupName: data.groupName.trim(),
      assignedUsers: selectedUsers,
    });
  };

  const handleCancel = () => {
    if (returnPath) {
      navigate(returnPath, { state: { activeTab: "user-group" } });
    } else {
      navigate("/admin/users", { state: { activeTab: "user-group" } });
    }
  };

  if (loadingInitialUsers) {
    return (
      <div className="container max-w-4xl mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-center py-8">
              <LoaderComponent />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div id="user_group_form_container" className="container max-w-4xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-semibold text-foreground">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form id="user_group_form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="groupName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>User Group Name *</FormLabel>
                    <FormControl>
                      <Input 
                        id="user_group_form_name_input"
                        placeholder="Enter group name" 
                        {...field} 
                        className="bg-background border-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <FormLabel>Users in this group ({selectedUsers.length}) *</FormLabel>
                  <Button
                    id="user_group_form_add_users_btn"
                    type="button"
                    variant="tertiary"
                    size="sm"
                    onClick={handleOpenModal}
                    className="flex items-center gap-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    Add Users
                  </Button>
                </div>
                {selectedUsers.length < 1 && (
                  <p className="text-sm text-destructive">At least 1 user is required</p>
                )}
                
                {/* Users in this group */}
                <div id="user_group_form_users_list" className="border border-input rounded-md bg-background">
                  <div className="max-h-48 overflow-y-auto">
                    {selectedUsers.length > 0 ? (
                      selectedUsers.map((user, index) => (
                        <div 
                          id={`user_group_form_user_row_${user.id}`}
                          key={user.id}
                          className={`flex items-center justify-between hover:bg-gray-50 py-3 px-4 ${index !== selectedUsers.length - 1 ? 'border-b' : ''}`}
                        >
                           <div>
                            <p className="text-sm font-medium text-foreground">{user.user_name}</p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{user.user_type}</Badge>
                            <Button
                              id={`user_group_form_remove_user_${user.id}_btn`}
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUserRemove(user.id)}
                              className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground p-4">No users assigned to this group. Start <a href="#" onClick={handleOpenModal} className="text-secondary underline">adding users</a>.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 justify-end">
                <Button 
                  id="user_group_form_cancel_btn"
                  type="button" 
                  variant="outline" 
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  id="user_group_form_submit_btn"
                  type="submit" 
                  disabled={isSubmitting || !form.formState.isValid || selectedUsers.length < 1 || (isEditMode && !formHasChanges)}
                  className="bg-primary text-primary-foreground hover:bg-primary-hover"
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Add Users Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent id="user_group_add_users_dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle id="user_group_add_users_dialog_title">Add Users to Group</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search and Filter Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  id="user_group_modal_search_input"
                  placeholder="Search (min 2 characters)"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="pl-10 bg-background border-input"
                />
              </div>
              
              <Select value={filterType} onValueChange={(value) => setFilterType(value as any)}>
                <SelectTrigger id="user_group_modal_filter_select" className="bg-background border-input">
                  <SelectValue placeholder="Search by" />
                </SelectTrigger>
                <SelectContent className="bg-background">
                  <SelectItem id="user_group_modal_filter_username" value="user_name">Username</SelectItem>
                  <SelectItem id="user_group_modal_filter_email" value="email">Email</SelectItem>
                  <SelectItem id="user_group_modal_filter_type" value="user_type">User Type</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Available Users List */}
            <div className="border border-input rounded-md p-4 bg-background">
              <h4 className="text-sm font-medium text-foreground mb-3">Select Users ({totalCount} total)</h4>
              {loading ? (
                <div className="flex justify-center py-8">
                  <LoaderComponent />
                </div>
              ) : (
                <>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {availableUsers.length > 0 ? (
                      availableUsers.map(user => {
                        const isSelected = tempSelectedUsers.some(selected => selected.id === user.id);
                        return (
                          <div 
                            key={user.id}
                            className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors ${
                              isSelected 
                                ? 'bg-primary/10 border border-primary' 
                                : 'hover:bg-accent'
                            }`}
                            onClick={() => handleModalUserSelect(user)}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="rounded border-input"
                              />
                              <div>
                                <p className="text-sm font-medium text-foreground">{user.user_name}</p>
                                <p className="text-xs text-muted-foreground">{user.email}</p>
                              </div>
                            </div>
                            <Badge variant="secondary">{user.user_type}</Badge>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-muted-foreground">No users available</p>
                    )}
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-4 pt-4 border-t border-input">
                      <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        recordsPerPage={entriesPerPage}
                        totalRecords={totalCount}
                        startIndex={(currentPage - 1) * entriesPerPage + 1}
                        endIndex={Math.min(currentPage * entriesPerPage, totalCount)}
                        onPageChange={setCurrentPage}
                        onRecordsPerPageChange={setEntriesPerPage}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button id="user_group_modal_cancel_btn" variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              id="user_group_modal_add_btn"
              onClick={handleAddUsers}
              disabled={tempSelectedUsers.length === 0}
              className="bg-primary text-primary-foreground hover:bg-primary-hover"
            >
              Add ({tempSelectedUsers.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};